import { useState } from 'react';
import { usePetSlots } from '../api/petSlots';
import { useCatalog, useCatalogPurchase, type CatalogEntryDto } from '../api/catalog';
import { useWallet } from '../api/wallet';
import { ApiClientError } from '../api/client';
import { ShellGlyph } from '../shell/glyphs';

const GENERIC_ERROR = 'Something went wrong — please try again.';

// Server rejection codes (PetSlotService.addPurchasedSlot, mapped by GlobalExceptionHandler —
// both 409) mapped to friendly, C3-neutral copy, same pattern every other friendlyXError in this
// app already uses: `pet_slot_already_owned` when a concurrent purchase (another tab, a retried
// tap) already granted this exact slot; `pet_slot_order` when the account's next slot in line
// isn't this one (slots unlock strictly in order 4 → 5 → 6, PetSlotService's own class javadoc).
function friendlyPurchaseError(err: unknown): string {
  if (err instanceof ApiClientError) {
    switch (err.code) {
      case 'insufficient_funds': return 'Not enough Shells for that.';
      case 'pet_slot_already_owned': return 'You already have this slot.';
      case 'pet_slot_order': return 'Slots unlock in order — grab the earlier one first.';
      default: return GENERIC_ERROR;
    }
  }
  return GENERIC_ERROR;
}

/**
 * SCREENS.md "/habitat — equipment additions (Phase 4)" "companion-slots-row": account-level
 * capacity — "Companions N/6" (`usePetSlots`, GET /companions/slots), used/unlocked, and a hint
 * about whatever unlocks next. Slot state is account-wide, not per-companion (petSlots.ts's own
 * header comment on why it's a separate module from companions.ts), so HabitatScreen renders this
 * exactly once — on the active companion's card, directly below its care meters, never repeated
 * per card even once Phase 3 lets more than one companion card render.
 *
 * `nextUnlock.kind`:
 * - `'level'` — the next slot is still locked behind a level threshold (10 for slot 2, 20 for slot
 *   3); informational hint only, no CTA (nothing to purchase yet).
 * - `'purchase'` — slots 4–6 are catalog SKUs (`slot_4`/`slot_5`/`slot_6`, PetSlotReward-driven —
 *   petSlots.ts's own doc comment). Rather than rebuild a purchase surface (there is no standalone
 *   catalog screen in this app yet to deep-link to — `useCatalog`/`useCatalogPurchase` are Task 12/
 *   14's existing, until-now-unused API module), this reuses those two hooks directly with the
 *   same inline fee-confirm shape AllocateSheet's respec row and ShopScreen's Buy flow already
 *   establish: tap → confirm card showing the entry's own server-priced cost + live wallet balance
 *   → Confirm, disabled while short (`insufficientReason`), same as every other paid confirm in
 *   this app. `null` when the account has already reached the hard cap of 6 — no hint, no CTA.
 *
 * Renders nothing while the slot-state query is loading or has failed: this row is a bonus
 * affordance layered onto an already-populated companion card, not core content worth its own
 * skeleton or retry card — same "quiet, no-op fallback" judgment call as a missing/broken item
 * icon elsewhere in this app never breaking layout.
 */
export function SlotsRow() {
  const petSlots = usePetSlots();
  const wallet = useWallet();
  const catalog = useCatalog();
  const purchase = useCatalogPurchase();

  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!petSlots.data) return null;
  const { used, unlocked, nextUnlock } = petSlots.data;

  const sku = nextUnlock?.kind === 'purchase' ? `slot_${nextUnlock.detail}` : null;
  const entry: CatalogEntryDto | undefined = sku ? catalog.data?.find((e) => e.sku === sku) : undefined;

  const levelHint = nextUnlock?.kind === 'level'
    ? `Next companion slot unlocks at level ${nextUnlock.detail}` : null;

  async function handlePurchase() {
    if (!sku || purchase.isPending) return;
    setError(null);
    try {
      await purchase.mutateAsync(sku);
      setConfirming(false);
    } catch (err) {
      setError(friendlyPurchaseError(err));
    }
  }

  const walletShells = wallet.data?.shells;
  const shortfall = entry && walletShells != null ? entry.price - walletShells : 0;
  const insufficientReason = entry && walletShells != null && shortfall > 0
    ? `You need ${shortfall} more Shells` : null;

  return (
    <div className="companion-slots-row">
      <span className="companion-slots-count">Companions: {used}/{unlocked}</span>
      {levelHint && <span className="companion-slots-hint">{levelHint}</span>}

      {nextUnlock?.kind === 'purchase' && (
        confirming ? (
          <div className="companion-slots-confirm">
            {entry ? (
              <>
                <p className="companion-slots-confirm-price">
                  Unlock another companion slot for <ShellGlyph /> {entry.price} Shells?
                </p>
                {insufficientReason && <p className="field-error" role="alert">{insufficientReason}</p>}
                <div className="companion-slots-confirm-actions">
                  <button type="button" className="btn-quiet" disabled={purchase.isPending} onClick={() => setConfirming(false)}>
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn-primary"
                    disabled={purchase.isPending || insufficientReason != null}
                    onClick={handlePurchase}
                  >
                    {purchase.isPending ? 'Unlocking…' : 'Confirm'}
                  </button>
                </div>
              </>
            ) : (
              <p className="form-note">Loading price…</p>
            )}
            {error && <p className="field-error" role="alert">{error}</p>}
          </div>
        ) : (
          <button type="button" className="btn-quiet companion-slots-purchase" onClick={() => setConfirming(true)}>
            Unlock a slot
          </button>
        )
      )}
    </div>
  );
}
